/**
 * Preview visual Lista Exprés / flyers — plantillas controladas (no editor libre).
 * Precios vía resolveExpresPrice (motor existente).
 */
import React from 'react';
import {
  buildEmpresaContacto,
  computeAhorroDisplay,
  getFormat,
  getTemplate,
  orderProducts,
  resolveExpresPrice
} from '../../../utils/listasExpresCatalog';
import { formatCurrencyAr } from '../../../utils/catalogoComercialExport';

const BG = {
  slate: 'linear-gradient(160deg,#0b1220 0%,#152238 55%,#1a2a44 100%)',
  forest: 'linear-gradient(160deg,#0d1f17 0%,#163528 50%,#1e4634 100%)',
  wine: 'linear-gradient(160deg,#1a0f14 0%,#3a1824 55%,#4a2230 100%)',
  sand: 'linear-gradient(160deg,#2a2418 0%,#3d3424 50%,#4a3f2c 100%)'
};

function stockOf(p) {
  const n = Number(p?.stock_actual ?? p?.stock ?? 0);
  return Number.isFinite(n) ? n : 0;
}

const ListaExpresPreview = React.forwardRef(function ListaExpresPreview(
  {
    productos = [],
    productOrder = [],
    priceListKey = 'mayorista',
    templateKey = 'lista_limpia',
    formatKey = 'a4_vertical',
    visibleFields = {},
    highlightedProductIds = [],
    commercialTitle = '',
    commercialSubtitle = '',
    validUntil = '',
    commercialConditions = '',
    cta = '',
    leyenda = '',
    empresaConfig = null,
    backgroundKey = 'slate',
    hidePriceIds = []
  },
  ref
) {
  const format = getFormat(formatKey);
  const template = getTemplate(templateKey);
  const contacto = buildEmpresaContacto(empresaConfig || {});
  const vf = visibleFields || {};
  const ordered = orderProducts(productos, productOrder).slice(0, template.maxProducts || 40);
  const highlight = new Set(highlightedProductIds || []);
  const hidePrice = new Set(hidePriceIds || []);
  const isFlyer = template.group === 'flyer' || template.group === 'whatsapp' || template.group === 'social';
  const scale = Math.min(1, 520 / format.width);

  return (
    <div className="overflow-auto max-w-full">
      <div
        style={{
          width: format.width * scale,
          height: format.height * scale,
          overflow: 'hidden'
        }}
      >
        <div
          ref={ref}
          data-testid="lista-expres-preview"
          style={{
            width: format.width,
            height: format.height,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            background: BG[backgroundKey] || BG.slate,
            color: '#f4f7fb',
            fontFamily: '"Segoe UI", system-ui, sans-serif',
            padding: isFlyer ? 48 : 40,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}
        >
          <header style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 24 }}>
            <div style={{ minWidth: 0 }}>
              {vf.logo !== false && contacto.logo ? (
                <img
                  src={contacto.logo}
                  alt=""
                  style={{ maxHeight: 56, maxWidth: 160, objectFit: 'contain', marginBottom: 8 }}
                />
              ) : null}
              {vf.empresa !== false && contacto.nombre ? (
                <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: 0.2 }}>{contacto.nombre}</div>
              ) : null}
              {vf.direccion !== false && contacto.direccion ? (
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>{contacto.direccion}</div>
              ) : null}
            </div>
            {vf.qr !== false && contacto.qrImageUrl ? (
              <img
                src={contacto.qrImageUrl}
                alt="QR"
                style={{ width: 88, height: 88, borderRadius: 8, background: '#fff', padding: 4 }}
              />
            ) : null}
          </header>

          {vf.titulo !== false && commercialTitle ? (
            <h1
              style={{
                fontSize: isFlyer ? 42 : 28,
                fontWeight: 800,
                margin: '0 0 8px',
                lineHeight: 1.15
              }}
            >
              {commercialTitle}
            </h1>
          ) : null}
          {vf.subtitulo !== false && commercialSubtitle ? (
            <p style={{ fontSize: isFlyer ? 20 : 15, opacity: 0.85, margin: '0 0 12px' }}>
              {commercialSubtitle}
            </p>
          ) : null}
          {vf.vigencia !== false && validUntil ? (
            <p style={{ fontSize: 13, opacity: 0.7, margin: '0 0 16px' }}>Vigencia hasta {validUntil}</p>
          ) : null}

          <div
            style={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns:
                template.key === 'flyer_uno'
                  ? '1fr'
                  : isFlyer
                    ? 'repeat(auto-fill, minmax(280px, 1fr))'
                    : template.key.includes('imagen') || template.key === 'novedades'
                      ? 'repeat(auto-fill, minmax(220px, 1fr))'
                      : '1fr',
              gap: isFlyer ? 20 : 10,
              alignContent: 'start',
              overflow: 'hidden'
            }}
          >
            {ordered.map((p) => {
              const precio = resolveExpresPrice(p, priceListKey);
              const ahorro =
                vf.precio_anterior !== false ? computeAhorroDisplay(p, priceListKey) : null;
              const dest = highlight.has(p.id);
              const showImg = vf.imagen !== false && template.key !== 'catalogo_sin_imagenes';
              const img = p.imagen_url || p.imagen || p.foto || '';
              return (
                <div
                  key={p.id}
                  style={{
                    border: dest ? '2px solid #f0b429' : '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 12,
                    padding: isFlyer ? 20 : 12,
                    background: dest ? 'rgba(240,180,41,0.08)' : 'rgba(255,255,255,0.04)',
                    display: 'flex',
                    gap: 12,
                    alignItems: template.key === 'flyer_uno' ? 'center' : 'flex-start'
                  }}
                >
                  {showImg ? (
                    img ? (
                      <img
                        src={img}
                        alt=""
                        style={{
                          width: template.key === 'flyer_uno' ? 220 : 72,
                          height: template.key === 'flyer_uno' ? 220 : 72,
                          objectFit: 'cover',
                          borderRadius: 8,
                          flexShrink: 0,
                          background: '#111'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: template.key === 'flyer_uno' ? 220 : 72,
                          height: template.key === 'flyer_uno' ? 220 : 72,
                          borderRadius: 8,
                          background: 'rgba(255,255,255,0.06)',
                          flexShrink: 0
                        }}
                      />
                    )
                  ) : null}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: template.key === 'flyer_uno' ? 28 : 14,
                        lineHeight: 1.25,
                        wordBreak: 'break-word'
                      }}
                    >
                      {p.nombre || 'Producto'}
                    </div>
                    {vf.codigo !== false && p.codigo ? (
                      <div style={{ fontSize: 11, opacity: 0.65, marginTop: 4 }}>{p.codigo}</div>
                    ) : null}
                    {vf.stock !== false ? (
                      <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>
                        Stock: {stockOf(p)}
                      </div>
                    ) : null}
                    {vf.precio !== false && !hidePrice.has(p.id) ? (
                      <div style={{ marginTop: 8 }}>
                        {ahorro ? (
                          <div style={{ fontSize: 12, opacity: 0.65, textDecoration: 'line-through' }}>
                            {formatCurrencyAr(ahorro.precioAnterior)}
                          </div>
                        ) : null}
                        <div
                          style={{
                            fontSize: template.key === 'flyer_uno' ? 36 : 18,
                            fontWeight: 800,
                            color: '#7dd3a0'
                          }}
                        >
                          {formatCurrencyAr(precio)}
                        </div>
                        {ahorro ? (
                          <div style={{ fontSize: 12, color: '#f0b429', marginTop: 2 }}>
                            Ahorrás {formatCurrencyAr(ahorro.monto)} ({ahorro.pct}%)
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>

          <footer style={{ marginTop: 20, fontSize: 12, opacity: 0.8 }}>
            {vf.cta !== false && cta ? (
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: '#f0b429' }}>{cta}</div>
            ) : null}
            {vf.leyenda !== false && leyenda ? <div style={{ marginBottom: 6 }}>{leyenda}</div> : null}
            {vf.condiciones !== false && commercialConditions ? (
              <div style={{ opacity: 0.7 }}>{commercialConditions}</div>
            ) : null}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
              {vf.whatsapp !== false && contacto.whatsapp ? <span>WA {contacto.whatsapp}</span> : null}
              {vf.email !== false && contacto.email ? <span>{contacto.email}</span> : null}
              {vf.web !== false && contacto.web ? <span>{contacto.web}</span> : null}
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
});

export default ListaExpresPreview;
